import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Paperclip,
  Upload,
  Trash2,
  FileText,
  Image,
  File,
  ExternalLink,
} from "lucide-react";
import type { TaskAttachment } from "@/features/tasks/hooks/useTaskDetail";
import { Spinner } from "@/shared/components/ui/Spinner";
import { useFormatDate } from "@/shared/hooks/useFormatDate";

interface TaskAttachmentsProps {
  attachments: TaskAttachment[];
  loading: boolean;
  uploading: boolean;
  onUpload: (file: File) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onGetUrl: (id: string) => Promise<string>;
}

function getFileIcon(fileType: string): React.ReactNode {
  if (fileType.startsWith("image/"))
    return <Image className="w-4 h-4 text-blue-500" />;
  if (fileType.includes("pdf"))
    return <FileText className="w-4 h-4 text-red-500" />;
  return <File className="w-4 h-4 text-gray-500" />;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const TaskAttachments: React.FC<TaskAttachmentsProps> = ({
  attachments,
  loading,
  uploading,
  onUpload,
  onDelete,
  onGetUrl,
}) => {
  const { t } = useTranslation();
  const { fmtDate } = useFormatDate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [openingId, setOpeningId] = useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await onUpload(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await onDelete(id);
    } finally {
      setDeletingId(null);
    }
  };

  const handleOpen = async (id: string) => {
    setOpeningId(id);
    try {
      const url = await onGetUrl(id);
      window.open(url, "_blank", "noopener,noreferrer");
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Upload button */}
      <div>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileChange}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 px-3 py-2 text-sm text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {uploading ? (
            <Spinner size="sm" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
          {uploading
            ? t("common.uploading", { defaultValue: "Uploading..." })
            : t("common.upload_file", { defaultValue: "Upload File" })}
        </button>
      </div>

      {/* Attachment list */}
      {loading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : attachments.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-8 text-gray-400">
          <Paperclip className="w-8 h-8" />
          <p className="text-sm">
            {t("common.no_attachments", { defaultValue: "No attachments" })}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {attachments.map((att) => (
            <div
              key={att.id}
              className="flex items-center gap-3 p-2.5 border border-gray-200 rounded-lg hover:bg-gray-50 group transition-colors"
            >
              {getFileIcon(att.file_type)}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {att.display_name}
                </p>
                <p className="text-xs text-gray-400">
                  {formatBytes(att.file_size)} · {fmtDate(att.created_at)}
                </p>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => handleOpen(att.id)}
                  disabled={openingId === att.id}
                  className="p-1.5 text-gray-400 hover:text-indigo-600 rounded-lg transition-colors"
                  title={t("common.view", { defaultValue: "View" })}
                >
                  {openingId === att.id ? (
                    <Spinner size="sm" />
                  ) : (
                    <ExternalLink className="w-3.5 h-3.5" />
                  )}
                </button>
                <button
                  onClick={() => handleDelete(att.id)}
                  disabled={deletingId === att.id}
                  className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg transition-colors"
                  title={t("common.delete", { defaultValue: "Delete" })}
                >
                  {deletingId === att.id ? (
                    <Spinner size="sm" />
                  ) : (
                    <Trash2 className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
